import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Auth } from 'aws-amplify';

interface Props {
  /** Email of the current morning user — pre-filled but editable. A different user
      can sign in here to become the new morning user. */
  defaultEmail: string;
  title: string;
  description: string;
  confirmLabel: string;
  /**
   * Weight of the confirm button. Defaults to `primary`, which is right where
   * re-authenticating is the whole point (switching workspace or user). Pass
   * `danger` where this screen is also the point of no return — clearing a PIN
   * signs the device out — so the colour sits on the click that does it rather
   * than on a warning somewhere upstream.
   */
  confirmVariant?: 'primary' | 'danger';
  /**
   * Called after Cognito re-auth succeeds. `sameUser` is true when the entered
   * email matches the current morning user (case-insensitive); false if someone
   * else just authenticated and the device session has rotated to them.
   */
  onConfirmed: (sameUser: boolean) => void;
  onCancel: () => void;
}

/**
 * Org Lock gate — proves whoever is at the device knows a valid Cognito password
 * before performing a sensitive action (currently only "Switch Workspace").
 *
 * Auth.signIn establishes a new device session, so signing in as a different
 * user atomically rotates the morning user. The caller decides what to do
 * with the `sameUser` signal (e.g. clear cached app-state for the prior user).
 */
const PasswordReauthModal: React.FC<Props> = ({ defaultEmail, title, description, confirmLabel, confirmVariant = 'primary', onConfirmed, onCancel }) => {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => { passwordRef.current?.focus(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || loading) return;
    setLoading(true);
    setError(null);
    try {
      await Auth.signIn(email, password);
      const sameUser = email.trim().toLowerCase() === defaultEmail.trim().toLowerCase();
      onConfirmed(sameUser);
    } catch (err: any) {
      setError(err?.message || 'Sign-in failed.');
      setPassword('');
      passwordRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Portal to document.body so the fixed overlay isn't trapped inside an
  // ancestor with a CSS transform (e.g. the sliding Account panel).
  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onCancel}
    >
      <div
        style={{
          // Opaque ivory paper, matching PinKeypad. Deliberately not the
          // shared .ivory-panel class — that one is translucent and goes
          // dingy over a dark scrim like this modal's.
          background: 'linear-gradient(180deg, #fffefa 0%, #f5f1e4 100%)',
          borderRadius: 16,
          padding: '2rem',
          minWidth: 360,
          maxWidth: 440,
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h5 style={{ marginBottom: '0.5rem', fontWeight: 700, color: '#14321a' }}>{title}</h5>
        <p style={{ fontSize: '0.85rem', color: '#4a5b48', lineHeight: 1.45, marginBottom: '1rem' }}>{description}</p>

        {error && (
          <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>{error}</div>
        )}

        <form onSubmit={submit}>
          <div className="mb-3">
            <label className="form-label">Email</label>
            <input
              className="form-control"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="username"
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Password</label>
            <input
              ref={passwordRef}
              className="form-control"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
            <button type="submit" className={`btn btn-${confirmVariant}`} disabled={loading || !email || !password}>
              {loading ? 'Verifying…' : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default PasswordReauthModal;
